import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SearchResult {
  id: string;
  title: string;
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  thumbnailUrl: string | null;
  subtitle: string;
  location: string | null;
  listingType: string;
}

interface SearchResults {
  helicopters: SearchResult[];
  engines: SearchResult[];
  parts: SearchResult[];
  jobs: SearchResult[];
  services: SearchResult[];
}

interface UseNavigationSearchReturn {
  results: SearchResults;
  isLoading: boolean;
  hasResults: boolean;
}

// Track search analytics (non-blocking)
const trackSearchAnalytics = async (queryText: string, resultsCount: number) => {
  try {
    const sessionId = sessionStorage.getItem('session_id') || crypto.randomUUID();
    sessionStorage.setItem('session_id', sessionId);

    const width = window.innerWidth;
    let deviceType = 'desktop';
    if (width < 640) deviceType = 'mobile';
    else if (width < 1024) deviceType = 'tablet';

    const { data: { user } } = await supabase.auth.getUser();

    await supabase.from('search_analytics').insert({
      query_text: queryText,
      asset_type: null, // Navigation search is cross-asset
      results_count: resultsCount,
      user_id: user?.id || null,
      session_id: sessionId,
      source: 'navigation',
      device_type: deviceType
    });

    import('@/lib/analytics/track').then(({ trackEvent }) =>
      trackEvent({
        event_name: resultsCount === 0 ? 'search_zero_results' : 'search_performed',
        event_category: 'search',
        properties: { query: queryText, results_count: resultsCount, source: 'navigation' },
      })
    );
  } catch (error) {
    // Non-blocking - don't interrupt search UX
    console.warn('Failed to track search analytics:', error);
  }
};

export function useNavigationSearch(query: string, debounceMs: number = 300): UseNavigationSearchReturn {
  const [results, setResults] = useState<SearchResults>({
    helicopters: [],
    engines: [],
    parts: [],
    jobs: [],
    services: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce the query
  useEffect(() => {
    // Set loading immediately when user types 2+ characters (before debounce)
    if (query.trim().length >= 2) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }

    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Search when debounced query changes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setResults({ helicopters: [], engines: [], parts: [], jobs: [], services: [] });
      setIsLoading(false);
      return;
    }

    const searchListings = async () => {
      setIsLoading(true);

      try {
        const escapeLike = (s: string) => s.replace(/%/g, '\\%').replace(/_/g, '\\_');
        const searchPattern = `%${escapeLike(debouncedQuery)}%`;
        const tsQuery = debouncedQuery.trim().split(/\s+/).join(' & ');

        // Search all six queries in parallel (helicopter cross-table + 5 listing tables)
        const [heliCrossResult, helicopterResult, engineResult, partsResult, jobsResult, servicesResult] = await Promise.allSettled([
          // Cross-table lookup for helicopter manufacturer/model
          supabase
            .from('helicopters')
            .select('id')
            .or(`manufacturer.ilike.${searchPattern},model.ilike.${searchPattern}`)
            .limit(20),

          // Helicopter listings — search by title/location first; cross-table IDs merged client-side
          supabase
            .from('helicopter_listings')
            .select(`
              id,
              title,
              location,
              images,
              listing_type,
              helicopter_id,
              helicopters!helicopter_listings_helicopter_id_fkey (
                manufacturer,
                model,
                icon_url
              )
            `)
            .eq('status', 'active')
            .eq('payment_status', 'paid')
            .or(`title.ilike.${searchPattern},location.ilike.${searchPattern}`)
            .limit(5),

          supabase
            .from('engine_listings')
            .select('id, title, manufacturer, model, city, country, photos, listing_type')
            .eq('status', 'active')
            .eq('payment_status', 'paid')
            .or(`search_vector.fts.${tsQuery},title.ilike.${searchPattern},manufacturer.ilike.${searchPattern},model.ilike.${searchPattern}`)
            .limit(5),

          supabase
            .from('parts_listings')
            .select('id, title, city, country, hero_image, listing_type, part_number, name, helicopter_manufacturer, engine_manufacturer')
            .eq('status', 'active')
            .eq('payment_status', 'paid')
            .or(`search_vector.fts.${tsQuery},part_number.ilike.${searchPattern},title.ilike.${searchPattern}`)
            .limit(5),

          supabase
            .from('job_listings')
            .select('id, title, company_name, job_category, city, country, photos, hero_image, listing_type')
            .eq('status', 'active')
            .eq('payment_status', 'paid')
            .or(`search_vector.fts.${tsQuery},title.ilike.${searchPattern},company_name.ilike.${searchPattern}`)
            .limit(5),

          supabase
            .from('service_listings')
            .select('id, title, company_name, service_category, city, country, hero_image, company_logo, listing_type')
            .eq('status', 'active')
            .eq('payment_status', 'paid')
            .or(`search_vector.fts.${tsQuery},title.ilike.${searchPattern},company_name.ilike.${searchPattern}`)
            .limit(5),
        ]);

        // Extract cross-table helicopter IDs and merge with direct results
        const matchingHelicopterIds: string[] = heliCrossResult.status === 'fulfilled' && !heliCrossResult.value.error
          ? (heliCrossResult.value.data || []).map((h: any) => h.id)
          : [];

        let helicopterData = helicopterResult.status === 'fulfilled' && !helicopterResult.value.error 
          ? helicopterResult.value.data || [] 
          : [];

        // If cross-table matched IDs not already in results, fetch those listings too
        if (matchingHelicopterIds.length > 0) {
          const existingIds = new Set((helicopterData as any[]).map((h: any) => h.id));
          const missingIds = matchingHelicopterIds.filter(id => !existingIds.has(id));
          // We don't need another query — the cross-table IDs matched helicopter_id in listings
          // Filter: any listing whose helicopter_id is in matchingHelicopterIds but wasn't in title/location results
          if (missingIds.length > 0 && (helicopterData as any[]).length < 5) {
            try {
              const { data: crossData } = await supabase
                .from('helicopter_listings')
                .select(`id, title, location, images, listing_type, helicopter_id,
                  helicopters!helicopter_listings_helicopter_id_fkey (manufacturer, model, icon_url)`)
                .eq('status', 'active')
                .eq('payment_status', 'paid')
                .in('helicopter_id', missingIds)
                .limit(5 - (helicopterData as any[]).length);
              if (crossData) {
                const combined = [...(helicopterData as any[])];
                for (const item of crossData) {
                  if (!existingIds.has(item.id)) {
                    combined.push(item);
                    existingIds.add(item.id);
                  }
                }
                helicopterData = combined;
              }
            } catch (e) {
              // Non-critical — direct results still available
            }
          }
        }
        const engineData = engineResult.status === 'fulfilled' && !engineResult.value.error 
          ? engineResult.value.data || [] 
          : [];
        const partsData = partsResult.status === 'fulfilled' && !partsResult.value.error 
          ? partsResult.value.data || [] 
          : [];
        const jobsData = jobsResult.status === 'fulfilled' && !jobsResult.value.error 
          ? jobsResult.value.data || [] 
          : [];
        const servicesData = servicesResult.status === 'fulfilled' && !servicesResult.value.error 
          ? servicesResult.value.data || [] 
          : [];

        // Collect all listing IDs to fetch their media
        const allListingIds = [
          ...(helicopterData as any[]).map((item: any) => item.id),
          ...(engineData as any[]).map((item: any) => item.id),
          ...(partsData as any[]).map((item: any) => item.id),
          ...(jobsData as any[]).map((item: any) => item.id),
          ...(servicesData as any[]).map((item: any) => item.id),
        ];

        // Fetch listing_media for all results in one query
        let mediaHeroById: Record<string, string> = {};
        if (allListingIds.length > 0) {
          try {
            const { data: mediaData } = await supabase
              .from('listing_media')
              .select('listing_id, file_url, media_type, display_order')
              .in('listing_id', allListingIds)
              .or('media_type.eq.photo,media_type.is.null')
              .order('display_order', { ascending: true });

            if (mediaData) {
              // Build map: first photo per listing = hero image
              for (const media of mediaData) {
                if (!mediaHeroById[media.listing_id] && media.file_url) {
                  mediaHeroById[media.listing_id] = media.file_url;
                }
              }
            }
          } catch (e) {
            console.warn('Failed to fetch listing media for search results:', e);
          }
        }

        // Helper to extract first valid URL from potentially malformed photo data
        const extractFirstValidUrl = (photos: any): string | null => {
          if (!photos) return null;
          const firstPhoto = Array.isArray(photos) ? photos[0] : photos;
          if (!firstPhoto || typeof firstPhoto !== 'string') return null;
          // If the string contains multiple URLs (space-separated), take the first one
          const urls = firstPhoto.trim().split(/\s+/);
          const validUrl = urls.find(url => url.startsWith('http'));
          return validUrl || null;
        };

        // Helper to get helicopter thumbnail with priority: listing_media → images → icon_url
        const getHelicopterThumbnail = (item: any): string | null => {
          // First: listing_media hero
          if (mediaHeroById[item.id]) return mediaHeroById[item.id];
          // Second: images array (legacy)
          const imageUrl = extractFirstValidUrl(item.images);
          if (imageUrl) return imageUrl;
          // Third: helicopter icon_url
          return item.helicopters?.icon_url || null;
        };

        // Transform helicopter results
        const helicopters: SearchResult[] = (helicopterData as any[]).map((item: any) => ({
          id: item.id,
          title: item.title,
          assetType: 'helicopter' as const,
          thumbnailUrl: getHelicopterThumbnail(item),
          subtitle: item.helicopters
            ? `${item.helicopters.manufacturer} ${item.helicopters.model}`
            : item.listing_type,
          location: item.location,
          listingType: item.listing_type,
        }));

        // Transform engine results
        const engines: SearchResult[] = (engineData as any[]).map((item: any) => ({
          id: item.id,
          title: item.title,
          assetType: 'engine' as const,
          thumbnailUrl: mediaHeroById[item.id] || extractFirstValidUrl(item.photos) || null,
          subtitle: `${item.manufacturer} ${item.model}`,
          location: [item.city, item.country].filter(Boolean).join(', ') || null,
          listingType: item.listing_type,
        }));

        // Transform parts results
        const parts: SearchResult[] = (partsData as any[]).map((item: any) => ({
          id: item.id,
          title: item.title,
          assetType: 'parts' as const,
          thumbnailUrl: item.hero_image || mediaHeroById[item.id] || null,
          subtitle: item.part_number || item.name || item.listing_type,
          location: [item.city, item.country].filter(Boolean).join(', ') || null,
          listingType: item.listing_type,
        }));

        // Transform jobs results
        const jobs: SearchResult[] = (jobsData as any[]).map((item: any) => ({
          id: item.id,
          title: item.title,
          assetType: 'jobs' as const,
          thumbnailUrl: item.hero_image || mediaHeroById[item.id] || extractFirstValidUrl(item.photos) || null,
          subtitle: item.company_name || item.job_category || item.listing_type,
          location: [item.city, item.country].filter(Boolean).join(', ') || null,
          listingType: item.listing_type,
        }));

        // Transform services results
        const services: SearchResult[] = (servicesData as any[]).map((item: any) => ({
          id: item.id,
          title: item.title,
          assetType: 'services' as const,
          thumbnailUrl: item.company_logo || item.hero_image || mediaHeroById[item.id] || null,
          subtitle: item.company_name || item.service_category || item.listing_type,
          location: [item.city, item.country].filter(Boolean).join(', ') || null,
          listingType: item.listing_type,
        }));

        // Client-side relevance re-ranking: title matches first
        const rerank = (items: SearchResult[]) => {
          const lower = debouncedQuery.toLowerCase();
          return items.sort((a, b) => {
            const aTitle = a.title.toLowerCase().includes(lower) ? 0 : 1;
            const bTitle = b.title.toLowerCase().includes(lower) ? 0 : 1;
            return aTitle - bTitle;
          });
        };

        setResults({
          helicopters: rerank(helicopters),
          engines: rerank(engines),
          parts: rerank(parts),
          jobs: rerank(jobs),
          services: rerank(services),
        });

        // Track search analytics (non-blocking)
        const totalResults = helicopters.length + engines.length + parts.length + jobs.length + services.length;
        trackSearchAnalytics(debouncedQuery, totalResults);
      } catch (error) {
        console.error('Search error:', error);
        setResults({ helicopters: [], engines: [], parts: [], jobs: [], services: [] });
      } finally {
        setIsLoading(false);
      }
    };

    searchListings();
  }, [debouncedQuery]);

  const hasResults = useMemo(() => {
    return results.helicopters.length > 0 || results.engines.length > 0 || results.parts.length > 0 || results.jobs.length > 0 || results.services.length > 0;
  }, [results]);

  return { results, isLoading, hasResults };
}
